import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import documentServices from 'services/documentServices'
import { downloadBlob } from 'utils/documentHelpers'
import SignPnDocumentViewer from './SignPnContract/SignPnDocumentViewer'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import ButtonSpinner from 'components/button-spinner/ButtonSpinner'
import download_interface_avarda_white from 'img/document-icons/download-interface-avarda-white.svg'
import { IDocuments } from 'interfaces/i-documents'
import { DocumentTypes } from 'constants/enums/documentTypes'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { ApplicationDispatchContext } from 'contexts/ApplicationContext'
import { getDeviceData } from 'utils/commonhelpers'
import pnServices from 'services/pnServices'
import { IAcceptOffer } from 'interfaces/i-accept-offer'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import { isOnlineIdentificationStatusSigned } from 'utils/loanHelpers'

export const SignPnUK = () => {
	const { t } = useTranslation()
	const { loanApplication }: IApplicationContext = useContext(
		ApplicationDispatchContext,
	)
	const applicationId = loanApplication.applicationData?.loanNumber!
	const { msp, dispatchMsp } = useContext(MspDispatchContext)

	const [error, setError] = useState<boolean>(false)
	const [docScale, setScale] = useState<number>(1.0)
	const [loading, setLoading] = useState<boolean>(true)
	const [loadingDownloadDoc, setLoadingDownloadDoc] = useState<boolean>(false)
	const [preContractDocument, setPreContractDocument] = useState<IDocuments>()
	const [preContractBlob, setPreContractBlob] = useState<Blob>()
	const [allDocumentsLoaded, setAllDocumentsLoaded] = useState<boolean>(false)
	const [termsApproved, setTermsApproved] = useState<boolean>(false)

	const loadRequiredDocuments = useCallback(async () => {
		setLoading(true)
		try {
			const response = await documentServices.getDownloadDocuments(
				applicationId.toString(),
				t,
			)
			loanApplication.setDocumentData(response)
			for (const document of response) {
				if (
					document.type === DocumentTypes.PrecontractCreditInformation
				) {
					setPreContractDocument(document)
					const { blob: blobWithoutType }: any =
						await documentServices.getDownloadDocumentById(
							String(document.documentId),
						)
					setPreContractBlob(
						new Blob([blobWithoutType], {
							type: 'application/pdf',
						}),
					)
				}
				// if there are more relevant document types here, add check and set variable/blob for them as well
			}
		} catch (error) {
			console.warn(error)
			setError(true)
		} finally {
			setLoading(false)
		}
	}, [applicationId, loanApplication, t])

	useEffect(() => {
		loadRequiredDocuments()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		// if there are more relevant document types here, check that all of them are defined
		if (preContractDocument !== undefined) {
			setAllDocumentsLoaded(true)
		}
	}, [preContractDocument])

	const downloadPreContract = async (fileName: string) => {
		if (preContractBlob) {
			try {
				setLoadingDownloadDoc(true)
				downloadBlob(preContractBlob as Blob, fileName)
			} catch (error) {
				console.warn(error)
				setError(true)
			} finally {
				setLoadingDownloadDoc(false)
			}
		}
	}

	async function signPreContract() {
		setLoading(true)
		try {
			const acceptOfferData: IAcceptOffer = {
				firstName: loanApplication.applicationData?.personalInfo?.firstName,
				lastName: loanApplication.applicationData?.personalInfo?.lastName,
				timestamp: new Date().toISOString(),
				apr: loanApplication.applicationData?.apr!,
				creditLimit: loanApplication.applicationData?.creditLimit!,
				// ssn: undefined, // we don't have ssn, and it's not mandatory in the ep anyway, so we skip this
				// ipAddress: undefined, // this will be populated in CDA
				deviceData: getDeviceData(),
				applicationId: applicationId,
			}
			await pnServices.acceptOffer(acceptOfferData)

			const onlineIdentificationUrl =
				loanApplication.applicationData?.signingUrls.onlineIdentification
					.url
			if (onlineIdentificationUrl) {
				window.location.href = onlineIdentificationUrl
			}

			next()
		} catch (error) {
			console.warn(error)
			setError(true)
			setLoading(false)
		}
	}

	useEffect(() => {
		// after redirect from Experian
		if (
			loanApplication.applicationData &&
			isOnlineIdentificationStatusSigned(loanApplication.applicationData!)
		) {
			next()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanApplication.applicationData])

	const next = () => {
		dispatchMsp({
			type: MSP_ACTIONS.SET_INDEX,
			payload: {
				index: msp.index + 1,
			},
		})
	}

	if (loading) {
		return (
			<div className="col-spinner">
				<CircleSpinner />
			</div>
		)
	}

	const errorMessage =
		!loanApplication.applicationData?.apr ||
		!loanApplication.applicationData?.creditLimit
			? t('ErrorMissingOfferData')
			: error
				? t('ErrorMsg')
				: !allDocumentsLoaded
					? t('ErrorFetchPn')
					: undefined

	if (errorMessage) {
		return (
			<div className="error-box-container">
				<ErrorBox message={errorMessage} />
			</div>
		)
	}

	return (
		<div className="sign-pn-view-contract-section">
			<div className="sign-pn-description">
				<div>
					<Trans t={t} i18nKey={'PromissoryNoteSigningLine1'}></Trans>
				</div>
				<div>
					<Trans t={t} i18nKey={'PromissoryNoteSigningLine2'}></Trans>
				</div>
			</div>
			{preContractBlob ? (
				<div id="view-pn-container">
					<SignPnDocumentViewer
						blob={preContractBlob}
						docScale={docScale}
						setScale={setScale}
					/>
				</div>
			) : (
				<ErrorBox message={t('ErrorViewPn')} />
			)}
			<div className="sign-pn-download-pn-section">
				<button
					id="sign-promissory-note-download-contract-button"
					className="btn btn-primary no-ripple download-pn-button"
					onClick={(e) => {
						e.preventDefault()
						setTimeout(() => {
							downloadPreContract(preContractDocument!.fileName)
						}, 400)
					}}
					disabled={loadingDownloadDoc}>
					{loadingDownloadDoc ? (
						<div className="pn-spinner-container">
							<ButtonSpinner />
						</div>
					) : (
						<>
							<div className="file-name">
								{t('DownloadPreContract')}
							</div>
							<img
								src={download_interface_avarda_white}
								alt="download img"
							/>
						</>
					)}
				</button>
			</div>
			<div className="sign-pn-approve-checkbox-section">
				<label className="checkbox-container">
					<input
						id="sign-promissory-note-confirm-checkbox"
						type="checkbox"
						className={`required-step ${
							termsApproved ? 'completed-step' : ''
						}`}
						checked={termsApproved}
						onChange={() => setTermsApproved(!termsApproved)}
					/>
					<span className="checkmark"></span>
					<span className="text">
						{t('PromissoryNoteApproveTerms')}
					</span>
				</label>
			</div>
			<div className="sign-pn-button-section">
				<button
					id="sign-promissory-note-sign-contract-button"
					type="button"
					className="btn btn-primary"
					disabled={!termsApproved}
					onClick={() => {
						setTimeout(() => {
							signPreContract()
						}, 400)
					}}>
					{t('SIGNPN')}
				</button>
			</div>
		</div>
	)
}
